import React, { useMemo } from 'react'
import { useRouter } from 'next/router'
import { useInfiniteQuery, useQueryClient } from 'react-query'

import { useStory } from '@hooks/book/useStory'
import { useAlert } from '@hooks/useAlert'
import { useMyWritingAction } from '@hooks/book/myWriting/useMyWritingAction'
import { BookStatusEnum } from '@interfaces/BookStatusEnum'
import { StoryDetailType } from '@models/story/StoryDetailType'
import { MenuBarProps } from './interface'

export function withMenuBar(Component: React.FC<MenuBarProps>) {
  function WithMenuBar() {
    const alert = useAlert()
    const myWritingClient = useMyWritingAction()
    const queryClient = useQueryClient()
    const { push, back } = useRouter()
    const { data } = useStory()

    function handleManageWriting() {
      push('/my-writing')
    }
    function handleEditWriting() {
      push(`/my-writing/${data?.id}/edit`)
    }
    function handleManageChapter() {
      push('#manage_chapters')
    }
    function previousPage() {
      back()
    }

    async function handleToggleIsEnded(e: React.ChangeEvent<HTMLInputElement>) {
      try {
        if (data) {
          const newIsEnded = e.target.checked
          await myWritingClient.updateBookIsEnded(data.id, newIsEnded)
          queryClient.setQueryData<StoryDetailType | undefined>(
            ['story-detail', data.id],
            (oldData?: StoryDetailType) => {
              if (oldData) {
                return {
                  ...oldData,
                  isEnded: newIsEnded,
                }
              }

              return oldData
            }
          )
          alert.success('แก้ไขสถานะงานเขียนสำเร็จ')
        }
      } catch (_) {
        alert.error('เกิดข้อผิดพลาด แก้ไขสถานะงานเขียนไม่สำเร็จ')
      }
    }

    const {
      data: myWriting,
      fetchNextPage,
      isFetchingNextPage,
    } = useInfiniteQuery(
      'my-writing-options',
      ({ pageParam = 1 }) => myWritingClient.getMyWritingOptions(pageParam),
      {
        getNextPageParam: (lastPage: any) => {
          if (lastPage && lastPage.data.length) return lastPage.page + 1

          return undefined
        },
      }
    )

    function handleFetchNextPage() {
      if (!isFetchingNextPage) fetchNextPage()
    }

    const myWritingOptions = useMemo(() => {
      return myWriting?.pages?.flatMap(page => page.data) ?? []
    }, [myWriting])

    const newProps = {
      title: data?.title ?? '',
      isPublished: data?.status === BookStatusEnum.PUBLISHED && data?.isVisible,
      isEnded: data?.isEnded,
      isVisible: data?.isVisible,
      myWritingOptions,
      handleFetchNextPage,
      handleManageWriting,
      handleEditWriting,
      handleManageChapter,
      handleToggleIsEnded,
      previousPage,
    }

    return <Component {...newProps} />
  }

  return WithMenuBar
}
